// A member who buys credits from the booking page goes to Stripe, which sends them back to
// /mypods when they've paid and /book when they cancel (success_url and cancel_url in the
// backend's checkoutBooking). Both used to lose the times they had picked (R1 #1, R2 #18).
// The booking page saves them here just before Stripe, and either way the member lands
// back on that booking page with them still selected.
import { pushPurchaseOnce } from './routes';

const KEY = 'gp_pending_booking';
const MAX_AGE_MS = 60 * 60 * 1000;
const STRIPE = 'https://checkout.stripe.com';
const BOOKING_PAGE = /^\/book\/[^/]+\/[^/]+$/;
// After a paid return, a Book & Pay tap that's still short re-checks the account instead
// of opening the packs, for this long (sessionStorage, so it outlives a refresh).
const HOLD_KEY = 'gp_paid_hold';
const HOLD_MS = 10 * 60 * 1000;

function read(storage, key) {
  try { return JSON.parse(window[storage].getItem(key) || 'null'); } catch (e) { return null; }
}
function write(storage, key, value) {
  try { window[storage].setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ }
}
function remove(storage, key) {
  try { window[storage].removeItem(key); } catch (e) { /* storage unavailable */ }
}

// The saved booking if it's under an hour old; an older or unreadable one is deleted.
function fresh() {
  const pending = read('localStorage', KEY);
  if (pending && pending.v === 1 && BOOKING_PAGE.test(pending.path) && Array.isArray(pending.selection)
    && Date.now() - pending.savedAt < MAX_AGE_MS) return pending;
  if (pending) remove('localStorage', KEY);
  return null;
}

function savePendingBooking(path, selection, total) {
  write('localStorage', KEY, { v: 1, path, selection, total, savedAt: Date.now() });
}

function clearPendingBooking() {
  remove('localStorage', KEY);
}

// Stripe's two return pages: success_url and cancel_url.
const RETURNS = { mypods: 'paid', book: 'cancelled' };

// main.js, before the first navigation. On the first load of /mypods (paid) or /book
// (cancelled) straight from Stripe, a member with a saved booking goes on to its booking
// page instead. A paid return first pushes `purchase`, as My PODs always has; My PODs
// doesn't open. Any other first load of those two pages drops the saved booking, so
// they behave as before.
function watchStripeReturn(router) {
  router.beforeEach((to, from) => {
    const returned = RETURNS[to.name];
    if (!returned || from.matched.length || window.__PRERENDER__) return true;
    if (!document.referrer.startsWith(STRIPE)) {
      clearPendingBooking();
      return true;
    }
    const pending = fresh();
    if (!pending) return true;
    if (returned === 'paid') pushPurchaseOnce();
    write('localStorage', KEY, { ...pending, returned });
    return { path: pending.path, replace: true };
  });
}

// The booking page: its saved booking ({ selection, total, returned }), once. Deleted as
// it's read, so it can't send the member back here again.
function takePendingBooking(path) {
  const pending = fresh();
  if (!pending || pending.path !== path) return null;
  remove('localStorage', KEY);
  return pending;
}

function setPaidHold(path, total) {
  write('sessionStorage', HOLD_KEY, { path, total, until: Date.now() + HOLD_MS });
}

// The paid hold for this booking page ({ total }), or null when there's none or it's over.
function paidHold(path) {
  const hold = read('sessionStorage', HOLD_KEY);
  if (hold && hold.path === path && Date.now() < hold.until) return hold;
  return null;
}

function clearPaidHold() {
  remove('sessionStorage', HOLD_KEY);
}

export {
  savePendingBooking,
  clearPendingBooking,
  watchStripeReturn,
  takePendingBooking,
  setPaidHold,
  paidHold,
  clearPaidHold,
};
